import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, mapTicket, mapResp } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { StatusFilter } from '../../components/StatusFilter';
import { PeriodFilter, usePeriodRange } from '../../components/PeriodFilter';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';


function TicketsPage() {
  const { user, clients, employees, showToast, tickets, setTickets, dbCreateTicket, dbAddTicketResponse, can, visibleCompanyIds, myCompanies, activeCompanyId, runSubmitting, notifFocus, setNotifFocus } = useApp();
  const [selTicket, setSelTicket] = useState(null);
  const [detailTicket, setDetailTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!notifFocus?.ticketId) return;
    setSelTicket(notifFocus.ticketId);
    setNotifFocus(null);
  }, [notifFocus?.ticketId, setNotifFocus]);
  const [newTicket, setNewTicket] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const range = usePeriodRange(period, customFrom, customTo);
  useEffect(() => {
    if (period === "all") { setFrom(""); setTo(""); return; }
    if (period === "custom") return;
    const fmt = (d) => d ? d.toISOString().split("T")[0] : "";
    setFrom(fmt(range.start));
    setTo(fmt(range.end));
  }, [period, customFrom, customTo, range.start, range.end]);
  const [form, setForm] = useState({ subject: "", description: "", priority: "medium", clientId: "" });
  const [response, setResponse] = useState("");

  const isTeam = user?.role === "admin" || user?.role === "manager" || user?.role === "employee";
  const isClient = user?.role === "client";
  const canFilterEmployee = user?.role === "admin" || user?.role === "manager";

  const clearFilters = () => { setSearch(""); setClientId(""); setEmployeeId(""); setFrom(""); setTo(""); setPeriod("all"); setCustomFrom(""); setCustomTo(""); };
  const localVisible = tickets.filter(t => {
    if (user?.role === "employee" && t.assignedTo !== user.id) return false;
    if (employeeId && t.assignedTo !== employeeId) return false;
    if (filter !== "all" && t.status !== filter) return false;
    if (clientId && t.clientId !== clientId) return false;
    if (search && !((t.subject || "").toLowerCase().includes(search.toLowerCase()) || (t.ticketNo || "").toLowerCase().includes(search.toLowerCase()) || (t.clientName || "").toLowerCase().includes(search.toLowerCase()))) return false;
    if (!dateInRange(t.createdAt, from, to)) return false;
    return true;
  });
  const apiFilter = buildApiFilter({
    equals: {
      ...(user?.role === "employee" ? { assigned_to: user.id } : {}),
      ...(employeeId ? { assigned_to: employeeId } : {}),
      ...(filter !== "all" ? { status: filter } : {}),
      ...(clientId ? { company_id: clientId } : {}),
    },
    from, to,
    dateField: "created_at",
    search,
    searchFields: ["subject", "ticket_no", "client_name"],
    order: "created_at.desc",
  });
  const { rows: visible, hasMore, loadMore, loadingMore } = usePaginatedApiRows("tickets", apiFilter, localVisible, { enabled: !!apiFilter });
  const visibleRef = useRef(visible);
  const ticketsRef = useRef(tickets);
  visibleRef.current = visible;
  ticketsRef.current = tickets;

  useEffect(() => {
    if (!selTicket) {
      setDetailTicket(null);
      setDetailLoading(false);
      return;
    }
    const cached = ticketsRef.current.find(tk => tk.id === selTicket)
      || visibleRef.current.find(tk => tk.id === selTicket);
    if (cached) setDetailTicket(cached);
    else setDetailTicket(null);

    let cancelled = false;
    setDetailLoading(!cached);
    (async () => {
      try {
        const rows = await sb.select('tickets', `id=eq.${selTicket}`);
        const raw = rows?.[0];
        if (cancelled) return;
        if (raw) {
          const t = mapTicket(raw);
          t.responses = (Array.isArray(raw.responses) ? raw.responses : []).map(mapResp);
          setDetailTicket(t);
        } else if (!cached) {
          setDetailTicket(null);
        }
      } catch {
        if (!cancelled && !cached) setDetailTicket(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selTicket]);

  const PRIORITY_COLORS = { high: "var(--red)", medium: "var(--gold-dk)", low: "var(--green)" };
  const STATUS_COLORS = { open: "var(--orange)", in_progress: "var(--blue)", resolved: "var(--green)", closed: "var(--muted)" };

  const statusOptions = [
    {
      id: "all",
      label: "All",
      count: tickets.length,
    },
    {
      id: "open",
      label: "Open",
      count: tickets.filter(t => t.status === "open").length,
    },
    {
      id: "in_progress",
      label: "Pending",
      count: tickets.filter(t => t.status === "in_progress").length,
    },
    {
      id: "resolved",
      label: "Resolved",
      count: tickets.filter(t => t.status === "resolved").length,
    },
    {
      id: "closed",
      label: "Closed",
      count: tickets.filter(t => t.status === "closed").length,
    },
  ];
  const createTicket = async () => {
    if (!form.subject) { showToast("Subject required", "error"); return; }
    // #G26 A client's ticket must bind to one of their OWN companies (respect the switcher), never a foreign one
    let clientId;
    if (isClient) {
      clientId = (form.clientId && visibleCompanyIds.includes(form.clientId)) ? form.clientId
        : (activeCompanyId || myCompanies[0]?.id || null);
      if (!clientId) { showToast("No company is linked to your account — contact support", "error"); return; }
    } else {
      clientId = form.clientId || null;
    }

    await runSubmitting('Raising ticket…', async () => {
    const t = {
      id: uuid(),
      ticketNo: "TK-" + Date.now().toString(36).toUpperCase(),
      clientId,
      clientName: clients.find(c => c.id === clientId)?.name || user?.name || "",
      raisedBy: user?.id || "",
      subject: form.subject, description: form.description,
      priority: form.priority, status: "open",
      assignedTo: employees[0]?.id || "",
      createdAt: today(), updatedAt: today(), responses: [], tat: null,
    };
    await dbCreateTicket(t);
    setNewTicket(false);
    setForm({ subject: "", description: "", priority: "medium", clientId: "" });
    showToast("Ticket raised! Team will see it within seconds.", "success");
    }, 'ticket');
  };

  const addResponse = async (ticketId) => {
    if (!response.trim()) return;
    await runSubmitting('Sending…', async () => {
      const resp = { id: uuid(), by: isTeam ? "team" : "client", text: response.trim(), date: today() };
      setResponse("");
      await dbAddTicketResponse(ticketId, resp);
      setDetailTicket(dt => dt?.id === ticketId ? {
        ...dt,
        responses: [...(dt.responses || []), resp],
        status: isTeam ? "in_progress" : dt.status,
        updatedAt: today(),
      } : dt);
      showToast("Response sent!", "success");
    }, 'ticket');
  };

  const resolveTicket = async (ticketId) => {
    try {
      await sb.update("tickets", "id", ticketId, { status: "resolved", updated_at: new Date().toISOString() });
      setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, status: "resolved", tat: "1 day", updatedAt: today() } : t));
      setSelTicket(null);
      showToast("Ticket resolved!", "success");
    } catch (e) {
      showToast(e.message || "Could not resolve ticket", "error");
    }
  };

  const employeeSummary = canFilterEmployee ? employees.map(emp => {
    const empTickets = tickets.filter(t => t.assignedTo === emp.id);
    return {
      emp,
      total: empTickets.length,
      open: empTickets.filter(t => t.status === "open").length,
      inProgress: empTickets.filter(t => t.status === "in_progress").length,
      resolved: empTickets.filter(t => !["open", "in_progress"].includes(t.status)).length,
    };
  }).filter(r => r.total > 0) : [];

  if (selTicket) {
    if (detailLoading && !detailTicket) {
      return (
        <>
          <button className="btn" style={{ marginBottom: 16 }} onClick={() => setSelTicket(null)}>← All Tickets</button>
          <div className="empty"><div>Loading ticket…</div></div>
        </>
      );
    }
    const t = detailTicket;
    if (!t) {
      return (
        <>
          <button className="btn" style={{ marginBottom: 16 }} onClick={() => setSelTicket(null)}>← All Tickets</button>
          <div className="empty"><div>Ticket not found</div></div>
        </>
      );
    }
    return (
      <>
        <button className="btn" style={{ marginBottom: 16 }} onClick={() => setSelTicket(null)}>← All Tickets</button>
        <div className="card">
          <div className="card-head">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="card-title">{t.subject}</div>
                <span className="tag" style={{ background: PRIORITY_COLORS[t.priority] + "20", color: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>{t.ticketNo} · {t.clientName} · Opened {t.createdAt}</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLORS[t.status], background: STATUS_COLORS[t.status] + "18", padding: "4px 10px", borderRadius: 20 }}>{t.status.replace("_", " ")}</span>
              {isTeam && t.status !== "resolved" && <button className="btn btn-green btn-sm" onClick={() => resolveTicket(t.id)}><InlineIcon name="check" size={13} gap={4}>Resolve</InlineIcon></button>}
            </div>
          </div>
          <div className="card-body">
            <div style={{ padding: "14px 16px", background: "var(--cream)", borderRadius: 10, marginBottom: 20, fontSize: 13, color: "var(--ink2)", lineHeight: 1.7 }}>
              {t.description}
            </div>
            {/* Responses */}
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>Conversation</div>
            {t.responses.length === 0 && <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>No responses yet.</div>}
            {t.responses.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14, justifyContent: r.by === "team" ? "flex-start" : "flex-end" }}>
                <div style={{ maxWidth: "80%", padding: "10px 14px", borderRadius: 10, background: r.by === "team" ? "var(--navy)" : "var(--gold-lt)", color: r.by === "team" ? "#fff" : "var(--ink)" }}>
                  <div style={{ fontSize: 10, opacity: .7, marginBottom: 4, textTransform: "uppercase", letterSpacing: "1px" }}>{r.by === "team" ? "Founders Bridge Team" : "You"} · {r.date}</div>
                  <div style={{ fontSize: 13 }}>{r.text}</div>
                </div>
              </div>
            ))}
            {t.status !== "resolved" && (
              <div>
                <textarea className="f-textarea" rows={3} placeholder="Type your response…" value={response} onChange={e => setResponse(e.target.value)} style={{ marginBottom: 10 }} />
                <button className="btn btn-primary" onClick={() => addResponse(t.id)}>Send Response →</button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search ticket # / subject / client…"
        clientId={clientId} setClientId={isTeam ? setClientId : undefined} clients={clients}
        from={from} setFrom={setFrom} to={to} setTo={setTo} onClear={clearFilters}
        extra={<PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} style={{ marginBottom: 0 }} />}
      />
      {canFilterEmployee && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Employee</label>
          <select className="f-select" style={{ maxWidth: 220 }} value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
            <option value="">All employees</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
          </select>
        </div>
      )}
      {canFilterEmployee && employeeSummary.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12, marginBottom: 20 }}>
          {employeeSummary.map(({ emp, total, open, inProgress, resolved }) => (
            <div key={emp.id} className="card" style={{ padding: "12px 14px", cursor: "pointer" }} onClick={() => setEmployeeId(emp.id)}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{emp.name}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.6 }}>
                {total} total · {open} open · {inProgress} pending · {resolved} closed
              </div>
            </div>
          ))}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <StatusFilter
          value={filter}
          onChange={setFilter}
          options={statusOptions}
          label="Status"
        />

        {(isClient || can("tickets", "create")) && (
          <button
            className="btn btn-primary"
            style={{ marginLeft: "auto" }}
            onClick={() => setNewTicket(true)}
          >
            {isClient ? "+ Raise Ticket" : "+ New Ticket"}
          </button>
        )}
      </div>
      {newTicket && (
        <div className="card card-gold" style={{ marginBottom: 20 }}>
          <div className="card-head"><div className="card-title">Raise a Ticket</div></div>
          <div className="card-body">
            <div className="form-grid">
              {isTeam && (
                <div className="f-group">
                  <label className="f-label">Client</label>
                  <select className="f-select" value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="f-group">
                <label className="f-label">Subject <span className="f-req">*</span></label>
                <input className="f-input" placeholder="Brief description of your query" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
              </div>
              <div className="f-group">
                <label className="f-label">Priority</label>
                <select className="f-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High — Urgent</option>
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">Description</label>
                <textarea className="f-textarea" rows={4} placeholder="Describe your query in detail…" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn" onClick={() => setNewTicket(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={createTicket}>Submit Ticket →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <div className="card-title">Tickets</div>
          {isTeam && (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              {tickets.filter(t => t.status === "open").length} open ·{" "}
              {tickets.filter(t => t.status === "in_progress").length} in progress ·{" "}
              Avg TAT: {tickets.filter(t => t.tat).length > 0 ? tickets.filter(t => t.tat).map(t => t.tat)[0] : "—"}
            </div>
          )}
        </div>
        {visible.length === 0 ? (
          <div className="empty"><div className="empty-icon"><Icon name="tickets" size={32} /></div><div>No tickets found</div></div>
        ) : (
          visible.map(t => (
            <div key={t.id} className={`ticket-row ticket-priority-${t.priority}`} onClick={() => setSelTicket(t.id)}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: STATUS_COLORS[t.status] + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                <Icon name={t.status === "resolved" ? "checkCircle" : "alert"} size={18} color={STATUS_COLORS[t.status]} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>{t.subject}</div>
                  <span style={{ fontSize: 10, background: PRIORITY_COLORS[t.priority] + "20", color: PRIORITY_COLORS[t.priority], padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{t.priority}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                  {t.ticketNo} · {t.clientName} · {t.createdAt}
                  {t.responses.length > 0 && <span style={{ marginLeft: 8 }}><InlineIcon name="message" size={11} gap={3}>{t.responses.length} response{t.responses.length !== 1 ? "s" : ""}</InlineIcon></span>}
                  {t.tat && <span style={{ marginLeft: 8, color: "var(--green)" }}><InlineIcon name="check" size={11} gap={3}>Resolved in {t.tat}</InlineIcon></span>}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_COLORS[t.status], background: STATUS_COLORS[t.status] + "18", padding: "3px 9px", borderRadius: 20, textTransform: "capitalize" }}>
                  {t.status.replace("_", " ")}
                </span>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Updated {t.updatedAt}</span>
              </div>
            </div>
          ))
        )}
        <LoadMoreBar hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} count={visible.length} />
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// EMPLOYEE EFFICIENCY ANALYTICS
// ═══════════════════════════════════════════════════════════════════════

export { TicketsPage };
